import React, { useState } from 'react';
import { Table, Row, Typography, Dropdown } from 'antd';
import { Colors } from 'works/legacy/lib/style-helper';
import {
  Link,
  Currency,
  Icon,
  InfoTooltip,
  TimeWithTimeZone,
} from '@common/components';
import { UPDATE_SHIFT_MUTATION } from '@scheduling/graphql';
import { stateDisplayMap } from '@scheduling/services';
import {
  PerformWorksShiftActionButton,
  ShiftCancelModal,
} from '@scheduling/components';
import { useMutation } from '@common/hooks';
import { createMessage } from 'works/legacy/lib/message-helper';
import { ShiftGapDetailsQueryCgResponse } from '@common/graphql/schema';

const { Text } = Typography;

type NewOpeningType = {
  key: string;
  label: string;
  state: string;
  id: undefined;
};

type ShiftOpeningType = NonNullable<
  ShiftGapDetailsQueryCgResponse['shiftGap']['shiftGapOpenings']
>[number];

export function ShiftOpeningsDrawerTable({
  shiftGapOpenings,
  count,
  shift,
  worksUser,
  loading,
  canAddOpenings,
}: {
  shiftGapOpenings: ShiftOpeningType[];
  count: number;
  shift: ShiftGapDetailsQueryCgResponse['shiftGap'];
  worksUser: { id: string } | undefined;
  loading: boolean;
  canAddOpenings?: boolean;
}) {
  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  const [cancelShiftId, setCancelShiftId] = useState<string | undefined>();

  const [updateShift, { loading: updateLoading }] = useMutation(
    UPDATE_SHIFT_MUTATION,
    {
      onCompleted: () => {
        createMessage('success', 'Shift has been updated!');
      },
      refetchQueries: [
        'ShiftGapDetails',
        'PaginatedShiftGapOpenings',
        'SpearfishableWorkersByShiftGap',
        'SpearfishableGroupsByShiftGap',
      ],
    }
  );

  const handleDelete = (id: string) => {
    onUpdate((shiftGapOpenings || []).filter((item) => item.id !== id));
  };

  const handleAdd = () => {
    const newOpening: NewOpeningType = {
      key: (count + 1).toString(),
      label: `${count}.`,
      state: 'open',
      id: undefined,
    };

    onUpdate([...shiftGapOpenings, newOpening]);
  };

  const onUpdate = (data: (ShiftOpeningType | NewOpeningType)[]) => {
    if (updateLoading) {
      return;
    }
    const openings = data.map((opening) => {
      return {
        uuid: opening?.id,
      };
    });

    const input = {
      shiftGapOpenings: openings,
      createdById: worksUser?.id || '',
    };

    updateShift({
      variables: {
        id: shift.id,
        input,
      },
    });
  };

  const isLoading = loading || updateLoading;

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: '30%',
      render: (id: string, record: ShiftOpeningType) => {
        if (!record.worksShift) {
          return <Text>----</Text>;
        }

        const worker = record.worksShift.worker;
        const workerName = worker.worksUser.name;
        const workerGroup = worker.workforceGroup?.name;

        return (
          <>
            <Row gutter={[0, 4]}>
              <Link to={`/works/team/on_demand/${worker.id}`}>
                <Text
                  style={{ color: Colors.elm, cursor: 'pointer' }}
                  className="sentry-mask"
                >
                  {workerName}
                </Text>
              </Link>
            </Row>
            <Row>
              <Text type="secondary">{workerGroup}</Text>
            </Row>
          </>
        );
      },
    },
    {
      title: (
        <>
          Rate
          <InfoTooltip
            size="sm"
            leftOffset={5}
            iconColor={Colors.elm}
            title="For external workers, rate is the bill rate. For internal workers, rate is any applicable incentive rate."
          />
        </>
      ),
      dataIndex: 'rate',
      key: 'rate',
      width: '30%',
      render: (id: string, record: ShiftOpeningType) => {
        if (!record.worksShift) {
          return <></>;
        }

        const worker = record.worksShift.worker;
        const rate = worker.isInternal
          ? record?.worksShift.perHourIncentivePay / 100.0
          : worker.allowLists?.find(
              (allowList) =>
                allowList.organizationUnit.id ===
                record?.worksShift?.shiftGap.organizationUnit.id
            )?.staffingRequestAssignment?.staffingRequest?.billingRegularRate ||
            record?.worksShift.perHourIncentivePay / 100.0;

        return (
          <>
            <Row gutter={[0, 4]}>
              <Currency value={rate} />
              /hr
            </Row>
          </>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: '25%',
      render: (id: string, record: ShiftOpeningType) => {
        if (record.canApproveReject?.value && record.worksShift) {
          return (
            <div className="row-start">
              <PerformWorksShiftActionButton
                worksShiftId={record.worksShift.id}
                type="reject"
              />
              <div style={{ width: 8 }} />
              <PerformWorksShiftActionButton
                worksShiftId={record.worksShift.id}
                type="confirm"
              />
            </div>
          );
        }

        const shiftState = record.worksShift
          ? record.worksShift.state
          : record.state;
        const displayStateChangedAt =
          record.state !== 'open' && record.stateChangedAt;

        return (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Text
              style={{
                fontWeight: 500,
                color:
                  stateDisplayMap?.[shiftState as keyof typeof stateDisplayMap]
                    ?.color,
              }}
            >
              {
                stateDisplayMap?.[shiftState as keyof typeof stateDisplayMap]
                  ?.label
              }
            </Text>

            {displayStateChangedAt && (
              <Text type="secondary">
                <TimeWithTimeZone
                  date={record.stateChangedAt || ''}
                  variant="mid_format"
                />
              </Text>
            )}
          </div>
        );
      },
    },
    {
      title: '',
      dataIndex: 'statusActions',
      key: 'statusActions',
      width: '15%',
      render: (_: null, record: ShiftOpeningType) => {
        if (record.worksShift && record.worksShift.canManagerCancel.value) {
          return (
            <div className="row-start">
              <div
                onClick={() => {
                  setCancelModalVisible(true);
                  setCancelShiftId(record.worksShift?.id);
                }}
                className={`button-actions ${isLoading && 'disabled'}`}
                style={{ color: Colors.coral }}
              >
                <Icon
                  icon="circleXmark"
                  kind="solid"
                  color="coral"
                  size="sm"
                  className="button-actions-icon"
                />
                Cancel
              </div>
            </div>
          );
        }

        if (
          record.canApproveReject?.value ||
          !canAddOpenings ||
          record.worksShift?.state
        ) {
          return null;
        }

        return (
          <div className="row-end">
            <Dropdown
              destroyPopupOnHide
              className="template-actions-dropdown"
              menu={{
                items: [
                  {
                    key: 'remove',
                    label: (
                      <a onClick={() => handleDelete(record.id)}>Remove</a>
                    ),
                  },
                ],
              }}
              trigger={['click']}
            >
              <a>
                <Icon
                  size="lg"
                  icon="verticalEllipsis"
                  kind="regular"
                  color="elm"
                />
              </a>
            </Dropdown>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <Table
        className="shift-openings-table"
        columns={columns}
        dataSource={shiftGapOpenings}
        pagination={false}
        expandable={{
          expandedRowClassName: () => 'shift-openings-table-expanded-row',
          expandedRowRender: (record, index) => {
            const showNote =
              record.canApproveReject?.value && !!record.worksShift?.workerNote;
            return (
              <div
                className="note-container"
                style={{
                  paddingBottom: showNote ? 24 : 0,
                  borderBottomWidth:
                    index !== shiftGapOpenings.length - 1 ? 1 : 0,
                }}
              >
                {showNote && (
                  <div className="note">{record.worksShift?.workerNote}</div>
                )}
              </div>
            );
          },
          defaultExpandAllRows: true,
          showExpandColumn: false,
        }}
      />
      {canAddOpenings && (
        <div style={{ display: 'flex' }}>
          <div
            onClick={handleAdd}
            className={`button-actions ${isLoading && 'disabled'}`}
          >
            <Icon
              icon="plus"
              kind="solid"
              color="elm"
              size="md"
              className="button-actions-icon"
            />
            Add Opening
          </div>
        </div>
      )}

      <ShiftCancelModal
        visible={cancelModalVisible}
        worksShiftId={cancelShiftId}
        setVisible={(value) => setCancelModalVisible(value)}
      />

      <style jsx>{`
        :global(.shift-openings-table) {
          margin-top: 24px;
        }
        :global(.shift-openings-table-expanded-row td) {
          background-color: white !important;
          padding: 0 !important;
        }
        :global(.note) {
          background-color: ${Colors.meadowHyperLight};
          padding: 16px;
          width: fit-content;
        }
        :global(.note-container) {
          border-bottom-style: solid;
          border-bottom-color: ${Colors.steel};
          width: 100%;
        }
        :global(.row-start) {
          display: flex;
          justify-content: flex-start;
          width: 100%;
        }
        :global(.row-center) {
          display: flex;
          justify-content: flex-start;
          align-items: center;
          width: 100%;
          flex-direction: column;
        }
        :global(.button-actions) {
          color: ${Colors.elm};
          margin: 0;
          cursor: pointer;
          display: flex;
          align-items: center;
        }
        :global(.button-actions.disabled) {
          color: ${Colors.steelDarker};
          pointer-events: none;
        }
        :global(.button-actions-icon) {
          padding-right: 5px;
        }
        :global(.works-shift-button-container) {
          display: flex;
        }
        :global(.shift-openings-drawer-title) {
          font-size: 16px;
          font-weight: 500;
        }
        :global(.shifts-openings-drawer-section) {
          cursor: pointer;
        }
        :global(.shift-openings-table) {
          margin-bottom: 16px;
        }
        :global(.shift-openings-table .ant-table-row) {
          cursor: default;
        }
        :global(.shift-openings-table .ant-table-tbody > tr > td) {
          border: none;
          padding: 24px 4px 24px 8px;
        }
        :global(.shift-openings-table .ant-table-thead > tr > th) {
          border: none;
          background-color: ${Colors.steelLightest};
          padding-top: 10px;
          padding-bottom: 10px;
          font-weight: 400;
        }
      `}</style>
    </>
  );
}
